/* TmaApp (DESIGN14 §F1.4) — the mini app's root.

   Boot sequence: resolve the Telegram host (real or dev mock) → ready() +
   expand() → paint the theme onto our CSS custom props (and keep watching
   themeChanged / viewportChanged) → exchange the Telegram initData for the
   normal session JWT via /api/tma/auth. On success the token is already
   seeded into the shared store (tmaApi), so we mount the real <AuthProvider>
   — it re-validates the token the SAME way the SPA does and hands the whole
   surface a useAuth() with the user + permissions, which the role helpers
   (estimateAccess / proposalAccess) read exactly like the web app.

   Every non-ready state is a friendly, white-label English screen: a loading
   splash, an "open me in Telegram" hint, the "not configured" notice, and the
   "ask an admin for an invite" screen. Nothing here leaks a model or provider
   name. */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AuthProvider, useAuth } from '../auth/AuthContext';
import {
  getWebApp,
  hasWebApp,
  loadTelegramSdk,
  applyTheme,
  watchTheme,
  watchViewport,
  haptic,
} from './telegram';
import { authTma } from './tmaApi';
import ProjectsScreen from './screens/ProjectsScreen';
import ProjectScreen from './screens/ProjectScreen';
import TeamScreen from './screens/TeamScreen';
import ApproversScreen from './screens/ApproversScreen';
import type { ApprovalStep } from '../architect/api';
import { WorkspaceDraftGuardProvider, useWorkspaceLeaveGuard } from '../architect/useWorkspaceDraftGuard';
import './tma.css';

type Phase =
  | { s: 'boot' }
  | { s: 'no-telegram' }
  | { s: 'auth' }
  | { s: 'error'; code: string }
  | { s: 'ready' };

/* ── the LATO wordmark (theme-independent gradient) ── */
function Wordmark() {
  return (
    <span className="tma-wordmark" aria-label="LATO">
      LATO
    </span>
  );
}

function Splash({ caption }: { caption?: string }) {
  return (
    <div className="tma-splash" role="status" aria-live="polite">
      <div className="tma-splash-mark">
        <Wordmark />
        <span className="tma-splash-pulse" aria-hidden="true" />
      </div>
      {caption ? <div className="tma-splash-caption">{caption}</div> : null}
    </div>
  );
}

interface StateScreenProps {
  emoji: string;
  title: string;
  text?: string;
  hint?: string;
  onRetry?: () => void;
  retryLabel?: string;
}

function StateScreen({
  emoji,
  title,
  text,
  hint,
  onRetry,
  retryLabel,
}: StateScreenProps) {
  return (
    <div className="tma-state">
      <div className="tma-state-card">
        <div className="tma-state-emoji" aria-hidden="true">
          {emoji}
        </div>
        <h1 className="tma-state-title">{title}</h1>
        {text ? <p className="tma-state-text">{text}</p> : null}
        {hint ? <p className="tma-state-hint">{hint}</p> : null}
        {onRetry ? (
          <button
            type="button"
            className="tma-btn tma-btn-primary tma-state-retry"
            onClick={() => {
              haptic.impact('light');
              onRetry();
            }}
          >
            {retryLabel || 'Try again'}
          </button>
        ) : null}
      </div>
    </div>
  );
}

// Machine code → friendly English screen. White-label throughout.
function ErrorScreen({ code, onRetry }: { code: string; onRetry: () => void }) {
  switch (code) {
    case 'tma_not_configured':
      return (
        <StateScreen
          emoji="🛠️"
          title="Mini App isn't set up yet"
          text="It will appear here as soon as an administrator finishes the setup."
        />
      );
    case 'not_linked':
      return (
        <StateScreen
          emoji="🔑"
          title="You need an invite link"
          text="Access is granted by an administrator — ask them to send you an invite link. It opens the mini app and links your account."
          onRetry={onRetry}
        />
      );
    case 'tma_not_allowed':
      return (
        <StateScreen
          emoji="🚫"
          title="No access"
          text="Your account doesn't have access to the mini app yet. Please contact an administrator."
        />
      );
    case 'user_disabled':
      return (
        <StateScreen
          emoji="⛔"
          title="Account disabled"
          text="This account is temporarily disabled. Please contact an administrator."
        />
      );
    case 'bad_init_data':
    case 'session_expired':
      return (
        <StateScreen
          emoji="🔒"
          title="Open the mini app again"
          text="Your sign-in has expired. Close the mini app and open it again from Telegram."
        />
      );
    case 'network':
      return (
        <StateScreen
          emoji="📡"
          title="No connection to the server"
          text="Check your connection and try again."
          onRetry={onRetry}
        />
      );
    default:
      return (
        <StateScreen
          emoji="😕"
          title="Couldn't sign in"
          text="Something went wrong. Please try again."
          onRetry={onRetry}
        />
      );
  }
}

/* ── the authenticated shell (inside AuthProvider) ── */
type Route =
  | { name: 'projects' }
  | { name: 'project'; id: string; step?: ApprovalStep | 'pricing'; tab?: 'poster' | 'estimate' | 'pricing' | 'proposal' }
  | { name: 'team' }
  | { name: 'approvers' };

const STEP_TAB: Record<ApprovalStep | 'pricing', 'poster' | 'estimate' | 'pricing' | 'proposal'> = {
  architecture: 'poster',
  estimate: 'estimate',
  pricing: 'pricing',
  proposal: 'proposal',
};

/* DESIGN42 — a notification's button opens the mini app with
   startapp=p_<project>_<step>; the sign-in ignores it for a linked account,
   the shell routes it: straight to that project, on that step's tab. */
const DEEP_LINK = /^p_([0-9a-fA-F-]{36})_(architecture|estimate|pricing|proposal)$/;

function initialRoute(): Route {
  const app = getWebApp();
  const raw = (app as any)?.initDataUnsafe?.start_param;
  const m = typeof raw === 'string' ? DEEP_LINK.exec(raw) : null;
  if (!m) return { name: 'projects' };
  const step = m[2] as 'architecture' | 'estimate' | 'pricing' | 'proposal';
  return { name: 'project', id: m[1], tab: step === 'architecture' ? 'poster' : step };
}

function TmaShell({ onSessionExpired }: { onSessionExpired: () => void }) {
  const { user, loading } = useAuth();
  const requestLeave = useWorkspaceLeaveGuard();
  const [route, setRoute] = useState<Route>(initialRoute);

  useEffect(() => {
    if (!loading && !user) onSessionExpired();
  }, [loading, user, onSessionExpired]);

  if (loading) return <Splash caption="Signing in…" />;
  if (!user)
    return (
      <StateScreen
        emoji="🔒"
        title="Session expired"
        text="Open the mini app from Telegram again."
      />
    );

  const back = () => {
    requestLeave(() => {
      haptic.impact('light');
      setRoute({ name: 'projects' });
    });
  };
  if (route.name === 'project') {
    return (
      <ProjectScreen
        key={route.id}
        projectId={route.id}
        initialTab={route.tab ?? (route.step ? STEP_TAB[route.step] : undefined)}
        onBack={back}
      />
    );
  }
  // Root-only screens — the server 403s anyone else, the list hides the pills.
  if (route.name === 'team' && user.isRoot) return <TeamScreen onBack={back} />;
  if (route.name === 'approvers' && user.isRoot) return <ApproversScreen onBack={back} />;
  return (
    <ProjectsScreen
      onOpen={(id) => {
        haptic.impact('light');
        setRoute({ name: 'project', id });
      }}
      onOpenStep={(id, step) => setRoute({ name: 'project', id, step })}
      onTeam={user.isRoot ? () => setRoute({ name: 'team' }) : undefined}
      onApprovers={user.isRoot ? () => setRoute({ name: 'approvers' }) : undefined}
    />
  );
}

export default function TmaApp() {
  const [phase, setPhase] = useState<Phase>({ s: 'boot' });
  const [sessionGeneration, setSessionGeneration] = useState(0);
  const [hostReady, setHostReady] = useState(false);
  const initDataRef = useRef<string>('');
  const authingRef = useRef(false);
  const recoveryAttemptedRef = useRef(false);

  // One-time host setup: load the SDK, then theme + viewport plumbing.
  useEffect(() => {
    let alive = true;
    let offTheme = () => {};
    let offVp = () => {};
    void loadTelegramSdk().then(() => {
      if (!alive) return;
      const app = getWebApp();
      // Outside Telegram the SDK still defines the object, with an EMPTY
      // initData — that is the «open it from Telegram» case, not a host.
      const outside = !app || (app.platform !== 'mock' && !app.initData);
      if (!app || outside) {
        // Paint the light-default theme vars so the "open in Telegram" screen
        // is styled even without a host.
        applyTheme(null);
        setPhase({ s: 'no-telegram' });
        return;
      }
      try {
        app.ready();
      } catch {
        /* older host */
      }
      try {
        app.expand();
      } catch {
        /* older host */
      }
      applyTheme(app);
      initDataRef.current = app.initData || '';
      offTheme = watchTheme(app);
      offVp = watchViewport(app);
      setHostReady(true);
    });
    return () => {
      alive = false;
      offTheme();
      offVp();
    };
  }, []);

  const authenticate = useCallback(async () => {
    if (!hasWebApp()) {
      setPhase({ s: 'no-telegram' });
      return;
    }
    if (authingRef.current) return;
    authingRef.current = true;
    setPhase({ s: 'auth' });
    try {
      const res = await authTma(initDataRef.current);
      if (res.ok) {
        haptic.impact('light');
        setSessionGeneration(value => value + 1);
        setPhase({ s: 'ready' });
      } else {
        setPhase({ s: 'error', code: res.code });
      }
    } finally {
      authingRef.current = false;
    }
  }, []);

  const recoverSession = useCallback(() => {
    if (authingRef.current) return;
    if (recoveryAttemptedRef.current) {
      setPhase({ s: 'error', code: 'session_expired' });
      return;
    }
    recoveryAttemptedRef.current = true;
    // A fresh Telegram exchange is attempted once. Failed API operations,
    // particularly writes, are never replayed after re-authentication.
    void authenticate();
  }, [authenticate]);

  useEffect(() => {
    if (!hostReady || !hasWebApp()) return;
    void authenticate();
  }, [hostReady, authenticate]);

  let body: React.ReactNode;
  if (phase.s === 'ready') {
    body = (
      <AuthProvider key={sessionGeneration}>
        <WorkspaceDraftGuardProvider>
          <TmaShell onSessionExpired={recoverSession} />
        </WorkspaceDraftGuardProvider>
      </AuthProvider>
    );
  } else if (phase.s === 'no-telegram') {
    body = (
      <StateScreen
        emoji="📱"
        title="Open in Telegram"
        text="This mini app runs inside Telegram. Open it from the invite link an administrator sent you."
      />
    );
  } else if (phase.s === 'error') {
    body = <ErrorScreen code={phase.code} onRetry={authenticate} />;
  } else {
    body = <Splash />;
  }

  return <div className="tma-app">{body}</div>;
}
